import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, Button, TextField, IconButton,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import AtsPipelineConfigurator from '../positions/AtsPipelineConfigurator';
import { buildDefaultPipeline } from '../positions/defaultPipeline';

/**
 * Plantillas de pipeline (ATS) — nivel proyecto.
 * Permite guardar configuraciones de pipeline con nombre para reutilizarlas al
 * crear vacantes. La marcada como predeterminada se precarga en el wizard.
 * Solo visible para admin hacia arriba (gating en AtsConfigurationLayout).
 * Las vacantes copian el pipeline al crearse: editar o eliminar una plantilla
 * no afecta vacantes existentes.
 */
const AtsPipelineTemplatesTab = () => {
  const { t } = useTranslation();
  const { organization, project, authToken, showAlert } = useStore.getState();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog de crear/editar
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = crear
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftPipeline, setDraftPipeline] = useState({ phases: [] });
  const [savingDialog, setSavingDialog] = useState(false);

  // Dialog de confirmación de borrado
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const authConfig = {
    params: { organizationId: organization, projectId: project },
    headers: { Authorization: `Bearer ${authToken}` },
  };

  useEffect(() => {
    const load = async () => {
      if (!organization || !project) { setLoading(false); return; }
      try {
        setLoading(true);
        const res = await api.get(`/projects/ats-pipeline-templates`, authConfig);
        const data = res.data.response?.data;
        setTemplates(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        showAlert(t('ats.config.pipelineTemplates.loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countStages = (tpl) =>
    (tpl.pipeline?.phases || []).reduce((acc, p) => acc + (p.stages?.length || 0), 0);

  // ── Crear / editar ────────────────────────────────────────────────────────

  const handleOpenCreate = () => {
    setEditingId(null);
    setDraftName('');
    setDraftDescription('');
    // Punto de partida: el pipeline estándar del sistema
    setDraftPipeline({ phases: buildDefaultPipeline().phases });
    setDialogOpen(true);
  };

  const handleOpenEdit = (tpl) => {
    setEditingId(tpl.id);
    setDraftName(tpl.name || '');
    setDraftDescription(tpl.description || '');
    setDraftPipeline({ phases: JSON.parse(JSON.stringify(tpl.pipeline?.phases || [])) });
    setDialogOpen(true);
  };

  const handleSaveDialog = async () => {
    const name = draftName.trim();
    if (!name) {
      showAlert(t('ats.config.pipelineTemplates.nameRequired'), 'warning');
      return;
    }
    if (!draftPipeline.phases?.length) {
      showAlert(t('ats.config.pipelineTemplates.phasesRequired'), 'warning');
      return;
    }
    setSavingDialog(true);
    try {
      const body = {
        name,
        description: draftDescription.trim(),
        pipeline: { phases: draftPipeline.phases },
      };
      let res;
      if (editingId) {
        res = await api.put(
          `/projects/ats-pipeline-templates/${editingId}`, body, authConfig
        );
      } else {
        res = await api.post(
          `/projects/ats-pipeline-templates`, body, authConfig
        );
      }
      if (res.data.response?.status_response === 'OK') {
        const saved = res.data.response.data;
        setTemplates((prev) => editingId
          ? prev.map((tpl) => (tpl.id === editingId ? saved : tpl))
          : [...prev, saved]);
        setDialogOpen(false);
        showAlert(t('ats.config.pipelineTemplates.saveSuccess'), 'success');
      } else {
        showAlert(t('ats.config.pipelineTemplates.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.config.pipelineTemplates.saveError'), 'error');
    } finally {
      setSavingDialog(false);
    }
  };

  // ── Predeterminada ────────────────────────────────────────────────────────

  const handleToggleDefault = async (tpl) => {
    const newValue = !tpl.isDefault;
    try {
      const res = await api.put(
        `/projects/ats-pipeline-templates/${tpl.id}`,
        { isDefault: newValue },
        authConfig
      );
      if (res.data.response?.status_response === 'OK') {
        // El backend garantiza una sola default; se replica localmente
        setTemplates((prev) => prev.map((x) => ({
          ...x,
          isDefault: newValue ? x.id === tpl.id : (x.id === tpl.id ? false : x.isDefault),
        })));
      } else {
        showAlert(t('ats.config.pipelineTemplates.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.config.pipelineTemplates.saveError'), 'error');
    }
  };

  // ── Eliminar ──────────────────────────────────────────────────────────────

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.delete(
        `/projects/ats-pipeline-templates/${deleteTarget.id}`, authConfig
      );
      if (res.data.response?.status_response === 'OK') {
        setTemplates((prev) => prev.filter((tpl) => tpl.id !== deleteTarget.id));
        showAlert(t('ats.config.pipelineTemplates.deleteSuccess'), 'success');
      } else {
        showAlert(t('ats.config.pipelineTemplates.deleteError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.config.pipelineTemplates.deleteError'), 'error');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        {t('ats.config.pipelineTemplates.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ats.config.pipelineTemplates.description')}
      </Typography>

      {templates.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ border: '1px dashed #D1D5DB', borderRadius: 2, p: 3, textAlign: 'center', mb: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('ats.config.pipelineTemplates.empty')}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {templates.map((tpl) => (
            <Paper
              key={tpl.id}
              elevation={0}
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: 2,
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {tpl.name}
                  </Typography>
                  {tpl.isDefault && (
                    <Chip
                      label={t('ats.config.pipelineTemplates.defaultChip')}
                      size="small"
                      sx={{ backgroundColor: '#6A5AF9', color: '#fff', height: 20, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
                {tpl.description && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {tpl.description}
                  </Typography>
                )}
              </Box>

              <Chip
                label={`${tpl.pipeline?.phases?.length || 0} ${t('ats.config.pipelineTemplates.phasesCount')}`}
                size="small"
                variant="outlined"
                sx={{ borderColor: '#6A5AF9', color: '#6A5AF9' }}
              />
              <Chip
                label={`${countStages(tpl)} ${t('ats.config.pipelineTemplates.stagesCount')}`}
                size="small"
                variant="outlined"
              />

              <Tooltip title={tpl.isDefault
                ? t('ats.config.pipelineTemplates.unsetDefault')
                : t('ats.config.pipelineTemplates.setDefault')}
              >
                <IconButton size="small" onClick={() => handleToggleDefault(tpl)}>
                  {tpl.isDefault
                    ? <StarIcon fontSize="small" sx={{ color: '#6A5AF9' }} />
                    : <StarBorderIcon fontSize="small" />}
                </IconButton>
              </Tooltip>

              <Tooltip title={t('ats.config.pipelineTemplates.edit')}>
                <IconButton size="small" onClick={() => handleOpenEdit(tpl)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('ats.config.pipelineTemplates.delete')}>
                <IconButton size="small" color="error" onClick={() => setDeleteTarget(tpl)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Box>
      )}

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleOpenCreate}
        sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
      >
        {t('ats.config.pipelineTemplates.add')}
      </Button>

      {/* Dialog crear/editar */}
      <Dialog
        open={dialogOpen}
        onClose={() => { if (!savingDialog) setDialogOpen(false); }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingId
            ? t('ats.config.pipelineTemplates.editTitle')
            : t('ats.config.pipelineTemplates.createTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
            <TextField
              size="small"
              label={t('ats.config.pipelineTemplates.nameField')}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              required
              sx={{ flex: '0 0 40%' }}
              inputProps={{ maxLength: 80 }}
            />
            <TextField
              size="small"
              label={t('ats.config.pipelineTemplates.descriptionField')}
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 200 }}
            />
          </Box>
          <Divider sx={{ mb: 2 }} />
          <AtsPipelineConfigurator
            pipeline={draftPipeline}
            onChange={setDraftPipeline}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={savingDialog}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDialog}
            disabled={savingDialog || !draftName.trim()}
            startIcon={savingDialog ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar eliminación */}
      <Dialog open={!!deleteTarget} onClose={() => { if (!deleting) setDeleteTarget(null); }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('ats.config.pipelineTemplates.deleteConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('ats.config.pipelineTemplates.deleteConfirmText', { name: deleteTarget?.name || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('ats.config.pipelineTemplates.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AtsPipelineTemplatesTab;
